import * as THREE from "three";

/**
 * An infinite, dashed grid rendered on a single axis plane ("xz", "xy" or "yz").
 *
 * The mesh is a single quad whose vertex shader re-centres and re-scales it around
 * the camera every frame, so the grid never runs out no matter how far the camera
 * travels. Lines, dashes and the distance fade are all computed in the fragment
 * shader in world space, which keeps them crisp at any zoom level.
 */
export class FloatingGrid extends THREE.Mesh {

    /** Default distance between grid lines in world units; also the step used for grid snapping. */
    static DEFAULT_CELL_SIZE = 1;

    static #axisIndex = { x: 0, y: 1, z: 2 };

    /** Plane name -> the two world axes the grid spans plus its normal axis. */
    static #planeAxes = {
        xz: ["x", "z", "y"],
        xy: ["x", "y", "z"],
        yz: ["y", "z", "x"]
    };

    /** Tint for the solid origin line running along each world axis. */
    static #axisColors = {
        x: 0xcc5555,
        y: 0x55bb55,
        z: 0x5588dd
    };

    /**
     * @param {object} [options]
     * @param {"xz"|"xy"|"yz"} [options.plane] Axis plane the grid lies on.
     * @param {number} [options.cellSize] Distance between grid lines in world units.
     * @param {THREE.ColorRepresentation} [options.color] Line colour.
     * @param {number} [options.opacity] Peak line opacity before the distance fade.
     * @param {number} [options.dashSize] Length of one dash + gap cycle in world units. Defaults to a fifth of a cell.
     * @param {number} [options.dashRatio] Fraction of the dash cycle that is drawn (0..1).
     */
    constructor({ plane = "xz", cellSize = FloatingGrid.DEFAULT_CELL_SIZE, color = 0x888888, opacity = 0.35, dashSize = cellSize / 5, dashRatio = 0.25 } = {}) {
        const axes = FloatingGrid.#planeAxes[plane];
        if (!axes) throw new Error(`Unknown grid plane '${plane}'. Expected "xz", "xy" or "yz".`);
        const [axisA, axisB, axisNormal] = axes;

        const geometry = FloatingGrid.#createPlaneGeometry(axisA, axisB);

        const material = new THREE.ShaderMaterial({
            uniforms: {
                uCellSize: { value: cellSize },
                uDashSize: { value: dashSize },
                uDashRatio: { value: dashRatio },
                uColor: { value: new THREE.Color(color) },
                uOpacity: { value: opacity },
                uMinExtent: { value: 200 },
                uAxisColorA: { value: new THREE.Color(FloatingGrid.#axisColors[axisA]) },
                uAxisColorB: { value: new THREE.Color(FloatingGrid.#axisColors[axisB]) },
                uOriginOpacity: { value: 0.9 },
                uMajorOpacityBoost: { value: 1.7 }
            },
            defines: {
                AXIS_A: axisA,
                AXIS_B: axisB,
                AXIS_N: axisNormal
            },
            vertexShader: /* glsl */`
                varying vec3 vWorldPos;
                uniform float uMinExtent;

                void main() {
                    // Grow the quad with the camera distance and keep it centred on the
                    // camera along the plane axes so the grid appears endless.
                    float extent = max(uMinExtent, length(cameraPosition) * 3.0);
                    vec3 pos = position * extent;
                    pos.AXIS_A += cameraPosition.AXIS_A;
                    pos.AXIS_B += cameraPosition.AXIS_B;
                    vWorldPos = pos;
                    gl_Position = projectionMatrix * viewMatrix * vec4(pos, 1.0);
                }`,
            fragmentShader: /* glsl */`
                varying vec3 vWorldPos;
                uniform float uCellSize;
                uniform float uDashSize;
                uniform float uDashRatio;
                uniform vec3 uColor;
                uniform float uOpacity;
                uniform float uMinExtent;
                uniform vec3 uAxisColorA;
                uniform vec3 uAxisColorB;
                uniform float uOriginOpacity;
                uniform float uMajorOpacityBoost;

                // Anti-aliased dashed line: 'coord' is the axis the lines repeat on
                // every 'cell' units, 'dashCoord' is the axis the dashes run along
                // with a dash cycle of 'dash' units.
                float gridLine(float coord, float dashCoord, float cell, float dash) {
                    float width = fwidth(coord);
                    float halfCell = 0.5 * cell;
                    float dist = abs(mod(coord + halfCell, cell) - halfCell);
                    float line = 1.0 - smoothstep(0.0, width, dist);

                    float dashPos = mod(dashCoord, dash) / dash;
                    float dashWidth = fwidth(dashCoord) / dash;
                    float dashed = 1.0 - smoothstep(uDashRatio - dashWidth, uDashRatio + dashWidth * 1.5, dashPos);
                    return line * dashed;
                }

                // Both line directions of a grid with the given cell size.
                float grid(float a, float b, float cell, float dash) {
                    return max(gridLine(a, b, cell, dash), gridLine(b, a, cell, dash));
                }

                // Solid, slightly thicker line at coord == 0 marking the world origin.
                float originLine(float coord) {
                    float width = fwidth(coord);
                    return 1.0 - smoothstep(0.0, width * 1.5, abs(coord) - width * 0.5);
                }

                void main() {
                    float a = vWorldPos.AXIS_A;
                    float b = vWorldPos.AXIS_B;

                    // Zoom level of detail: fade the 1-unit cells out as they shrink on
                    // screen and hand over to cells 10x the size.
                    float pixelsPerCell = uCellSize / max(fwidth(a), fwidth(b));
                    float minorFade = smoothstep(4.0, 12.0, pixelsPerCell);
                    float minor = grid(a, b, uCellSize, uDashSize) * minorFade;
                    float major = grid(a, b, uCellSize * 10.0, uDashSize * 10.0);

                    float originA = originLine(b); // line running along AXIS_A, at AXIS_B == 0
                    float originB = originLine(a); // line running along AXIS_B, at AXIS_A == 0
                    float origin = max(originA, originB);

                    float extent = max(uMinExtent, length(cameraPosition) * 3.0);
                    float fade = 1.0 - smoothstep(extent * 0.35, extent * 0.85, distance(cameraPosition, vWorldPos));

                    // Origin lines override the dashed grid where they overlap.
                    vec3 colour = mix(uColor, originA >= originB ? uAxisColorA : uAxisColorB, origin);
                    float gridIntensity = max(minor * uOpacity, major * min(uOpacity * uMajorOpacityBoost, 1.0));
                    float intensity = max(gridIntensity, origin * uOriginOpacity);

                    float alpha = intensity * fade;
                    if (alpha <= 0.002) discard;
                    gl_FragColor = vec4(colour, alpha);
                }`,
            transparent: true,
            depthWrite: false,
            side: THREE.DoubleSide
        });

        super(geometry, material);

        // The vertex shader repositions the quad, so the static bounds are meaningless.
        this.frustumCulled = false;
        this.renderOrder = -1;
    }

    /** The grid is decorative; never let it intercept picking rays. */
    raycast() { }

    dispose() {
        this.geometry.dispose();
        this.material.dispose();
    }

    /** Unit quad lying on the plane spanned by the two given world axes. */
    static #createPlaneGeometry(axisA, axisB) {
        const corners = [[-1, -1], [1, -1], [1, 1], [-1, 1]];
        const positions = new Float32Array(corners.length * 3);
        for (let i = 0; i < corners.length; i++) {
            positions[i * 3 + FloatingGrid.#axisIndex[axisA]] = corners[i][0];
            positions[i * 3 + FloatingGrid.#axisIndex[axisB]] = corners[i][1];
        }

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
        geometry.setIndex([0, 1, 2, 0, 2, 3]);
        return geometry;
    }
}
